import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addressService } from '../../services/addressService';
import AddressCard from './AddressCard';
import AddressForm from './AddressForm';
import ConfirmModal from '../common/ConfirmModal';
import './AddressSelector.css';

/**
 * AddressSelector Component
 * Main component for selecting/managing delivery addresses
 */
export default function AddressSelector({
  onAddressSelect,
  selectedAddressId = null,
}) {
  // State
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, addressId: null });

  // Fetch addresses on mount
  const fetchAddresses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await addressService.getAddresses();
      if (response.success) {
        setAddresses(response.data || []);

        // Auto-select default address if none selected
        if (!selectedAddressId && response.data?.length > 0) {
          const defaultAddr = response.data.find(a => a.isDefault) || response.data[0];
          if (onAddressSelect) {
            onAddressSelect(defaultAddr);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
      setError('Failed to load addresses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedAddressId, onAddressSelect]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  // Handle address selection
  const handleSelect = (address) => {
    if (onAddressSelect) {
      onAddressSelect(address);
    }
  };

  // Handle add new address
  const handleAddNew = () => {
    setEditingAddress(null);
    setShowForm(true);
  };

  // Handle edit address
  const handleEdit = (address) => {
    setEditingAddress(address);
    setShowForm(true);
  };

  // Handle delete address
  const handleDelete = (addressId) => {
    setDeleteModal({ isOpen: true, addressId });
  };

  const confirmDelete = async () => {
    const addressId = deleteModal.addressId;
    try {
      const response = await addressService.deleteAddress(addressId);
      if (response.success) {
        setAddresses(prev => prev.filter(a => a.id !== addressId));

        // If deleted address was selected, clear selection
        if (selectedAddressId === addressId && onAddressSelect) {
          const remaining = addresses.filter(a => a.id !== addressId);
          onAddressSelect(remaining[0] || null);
        }
      }
    } catch (err) {
      console.error('Failed to delete address:', err);
    }
  };

  // Handle set default
  const handleSetDefault = async (addressId) => {
    try {
      const response = await addressService.setDefaultAddress(addressId);
      if (response.success) {
        setAddresses(prev => prev.map(a => ({
          ...a,
          isDefault: a.id === addressId,
        })));
      }
    } catch (err) {
      console.error('Failed to set default:', err);
      alert('Failed to set default address. Please try again.');
    }
  };

  // Handle form submit
  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      let response;
      if (editingAddress) {
        response = await addressService.updateAddress(editingAddress.id, formData);
      } else {
        response = await addressService.createAddress(formData);
      }

      if (response.success) {
        await fetchAddresses();
        setShowForm(false);
        setEditingAddress(null);

        // Select the new/updated address
        if (onAddressSelect) {
          onAddressSelect(response.data);
        }
      }
    } catch (err) {
      console.error('Failed to save address:', err);
      alert(err.response?.data?.message || 'Failed to save address. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAddress(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="address-selector">
        <div className="address-selector-loading">
          <div className="loading-spinner"></div>
          <p>Loading addresses...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="address-selector">
        <div className="address-selector-error">
          <p>{error}</p>
          <button onClick={fetchAddresses}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="address-selector">
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, addressId: null })}
        onConfirm={confirmDelete}
        title="Delete Address"
        message="Are you sure you want to delete this address? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <div className="address-selector-header">
        <h2 className="section-title">Delivery Address</h2>
        {!showForm && (
          <button className="add-address-btn" onClick={handleAddNew}>
            + Add New Address
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {showForm ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <AddressForm
              initialData={editingAddress}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isLoading={formLoading}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {addresses.length === 0 ? (
              <div className="no-addresses">
                <div className="no-addresses-icon">📍</div>
                <h3>No saved addresses</h3>
                <p>Add a delivery address to continue</p>
                <button className="add-first-address-btn" onClick={handleAddNew}>
                  Add Your First Address
                </button>
              </div>
            ) : (
              <div className="address-list">
                {addresses.map(address => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    isSelected={selectedAddressId === address.id}
                    onSelect={handleSelect}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSetDefault={handleSetDefault}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
